import axios from "axios";
import {
  API_CONTACT_REQUESTS_ROUTE,
  API_PLATFORM_EVALUATIONS_ROUTE,
} from "../global/constants/routes/apiConstants";
import {
  ContactRequestInput,
  ContactRequestPayload,
  ContactRequestResponse,
  FaleConoscoFastApiErrorResponse,
  FaleConoscoServiceError,
  PlatformEvaluationInput,
  PlatformEvaluationPayload,
  PlatformEvaluationResponse,
} from "../global/types/faleConosco";
import { FastApiValidationDetail } from "../global/types/registrationRequest";
import { apiService } from "./api";

const CONFLICT_MESSAGE =
  "Já existe um envio recente com estes dados. Aguarde alguns minutos antes de tentar novamente.";
const VALIDATION_MESSAGE =
  "Não foi possível validar os dados enviados. Revise os campos e tente novamente.";
const DELIVERY_FAILED_MESSAGE =
  "Os dados foram registrados, mas não foi possível notificar a equipe neste momento.";
const TEMPORARY_ERROR_MESSAGE =
  "O serviço está temporariamente indisponível. Tente novamente.";
const UNKNOWN_CONTACT_ERROR_MESSAGE =
  "Não foi possível enviar a mensagem. Tente novamente.";
const UNKNOWN_EVALUATION_ERROR_MESSAGE =
  "Não foi possível enviar a avaliação. Tente novamente.";

const toContactPayload = (
  input: ContactRequestInput
): ContactRequestPayload => ({
  full_name: input.fullName,
  email: input.email,
  message: input.message,
  lgpd_consent: input.lgpdConsent,
});

const toEvaluationPayload = (
  input: PlatformEvaluationInput
): PlatformEvaluationPayload => ({
  full_name: input.fullName || null,
  email: input.email || null,
  general_experience_rating: input.generalExperienceRating,
  information_relevance_rating: input.informationRelevanceRating,
  crossing_ease_rating: input.crossingEaseRating,
  report_clarity_rating: input.reportClarityRating,
  planning_support_rating: input.planningSupportRating,
  institutional_usefulness_rating: input.institutionalUsefulnessRating,
  lgpd_consent: input.lgpdConsent,
});

const getValidationDetails = (
  data: FaleConoscoFastApiErrorResponse | undefined
): FastApiValidationDetail[] => {
  const detail = data?.detail;
  return Array.isArray(detail) ? detail : [];
};

const normalizeError = (
  error: unknown,
  unknownErrorMessage: string
): FaleConoscoServiceError => {
  if (!axios.isAxiosError<FaleConoscoFastApiErrorResponse>(error)) {
    return new FaleConoscoServiceError("UNKNOWN", unknownErrorMessage);
  }

  if (!error.response) {
    return new FaleConoscoServiceError("NETWORK", TEMPORARY_ERROR_MESSAGE);
  }

  const { status, data } = error.response;
  if (status === 409) {
    return new FaleConoscoServiceError("CONFLICT", CONFLICT_MESSAGE, status);
  }

  if (status === 422) {
    return new FaleConoscoServiceError(
      "VALIDATION",
      VALIDATION_MESSAGE,
      status,
      getValidationDetails(data)
    );
  }

  if (status === 502) {
    return new FaleConoscoServiceError(
      "DELIVERY_FAILED",
      DELIVERY_FAILED_MESSAGE,
      status
    );
  }

  return new FaleConoscoServiceError("UNKNOWN", unknownErrorMessage, status);
};

export const createContactRequest = async (
  input: ContactRequestInput
): Promise<ContactRequestResponse> => {
  try {
    const response = await apiService.post<ContactRequestResponse>(
      API_CONTACT_REQUESTS_ROUTE,
      toContactPayload(input)
    );
    return response.data;
  } catch (error) {
    throw normalizeError(error, UNKNOWN_CONTACT_ERROR_MESSAGE);
  }
};

export const createPlatformEvaluation = async (
  input: PlatformEvaluationInput
): Promise<PlatformEvaluationResponse> => {
  try {
    const response = await apiService.post<PlatformEvaluationResponse>(
      API_PLATFORM_EVALUATIONS_ROUTE,
      toEvaluationPayload(input)
    );
    return response.data;
  } catch (error) {
    throw normalizeError(error, UNKNOWN_EVALUATION_ERROR_MESSAGE);
  }
};
